import { ref } from 'vue'
import type { CollectionPlayer } from '@lib/apps/collection-player/types/collection-player.type'

export interface CollectionPlayerOverlayGroup {
  id: string
  name: string
  slides?: Array<{
    file_id: string
    slide_index: number
  }>
}

export interface CollectionPlayerOverlay {
  groups: CollectionPlayerOverlayGroup[]
  name: string
}

const setCollectionPlayerData = ref<((data: CollectionPlayer) => void) | null>(null)

export default () => {
  const openCollectionPlayerOverlay = async (
    data: CollectionPlayerOverlay,
    fetchFile: (fileId: string) => Promise<any>,
  ) => {
    if (!setCollectionPlayerData.value) return

    const normalizedData = await normalizeData(data, fetchFile)
    setCollectionPlayerData.value(normalizedData)
  }

  return {
    setCollectionPlayerData,
    openCollectionPlayerOverlay,
  }
}

async function normalizeData(
  data: CollectionPlayerOverlay,
  fetchFile: (fileId: string) => Promise<any>,
): Promise<CollectionPlayer> {
  const fileCache = new Map<string, any>()

  const getFile = async (fileId: string) => {
    if (fileCache.has(fileId)) {
      return fileCache.get(fileId)
    }
    const file = await fetchFile(fileId)
    fileCache.set(fileId, file)
    return file
  }

  const processedGroups = await Promise.all(
    data.groups.map(async (group) => {
      let slides
      if (group.slides?.length) {
        const slideResults = await Promise.allSettled(
          group.slides.map(async (slide) => {
            const file = await getFile(slide.file_id)
            return {
              id: `${slide.file_id}-${slide.slide_index}`,
              type: 'slide' as const,
              file,
              slide: {
                index: slide.slide_index,
                url: file.content_thumbnails[slide.slide_index],
              },
            }
          }),
        )
        slides = slideResults
          .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
          .map((result) => result.value)
      } else {
        try {
          const file = await getFile(group.id)
          slides =
            file.content_type === 'pdf'
              ? file.content_thumbnails.map((url: string, index: number) => ({
                  id: `${group.id}-${index}`,
                  type: 'slide' as const,
                  file,
                  slide: {
                    index,
                    url,
                  },
                }))
              : [
                  {
                    id: group.id,
                    type: 'file' as const,
                    file,
                  },
                ]
        } catch (error) {
          console.error(`Failed to fetch file for group ${group.id}:`, error)
          slides = []
        }
      }

      return {
        id: group.id,
        name: group.name,
        slides,
      }
    }),
  )

  return {
    groups: processedGroups,
    name: data.name,
  }
}
